import {useTheme} from "@emotion/react";
import {useLoaderData, useNavigation} from "@remix-run/react";
import {useEffect, useState} from "react";
import LoadingBar from "react-top-loading-bar";
import {NewsLetterModal} from "../../components/NewsLetter/index.jsx";
import {Ad, BASE_AD_TAG} from "../../components/adSections/index.jsx";
import B1Herobanner from "../../components/sections/B1Herobanner/index.jsx";
import HeroTransparent from "../../components/sections/HeroTransparent";
import HeroTransparentText from "../../components/sections/HeroTransparentText";
import useHasHydrated from "../../hooks/useHasHydrated.js";
import {localizeBreadcrumbMagazineName} from "../../utils/breadcrumb.js";
import {decodeHtmlEntities} from "../../utils/text.js";
import Breadcrumbs from "../Breadcrumbs";
import Footer from "../Footer";
import Header from "../Header";
import Navigation from "../Navigation";
import StickyBar from "../StickyBar";
import {LayoutWrapper} from "./styles";

const BasePage = ({
  children,
  heroBannerData,
  breadcrumb = {},
  headerType = "white",
  headerData,
  wordCount,
  isLegalPage = false,
  isSearchPage = false,
  isContactPage = false,
  isDestinationPage = false,
  isArticlePage = false,
  isHomePage = false,
  isFaqPage = false,
  isDestinationsPage = false,
  adsEnabled,
  translation,
}) => {
  const hasHydrated = useHasHydrated();
  const data = useLoaderData();
  const footerData = data?.footer?.footer;

  const footerLinks = data?.footerData;
  const basename = data?.basename;

  const language = data?.language;

  const secondLevelFooterLinks = data?.secondFooterData;
  const [showMenu, setShowMenu] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [showFixedHeader, setShowFixedHeader] = useState(false);
  const {breakpoints} = useTheme();
  const {isMobile} = breakpoints;

  const isTransparent = headerType === "transparent";

  const navigation = useNavigation();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (navigation.state === "idle") {
      setProgress(100);
    }
    if (navigation.state === "loading") {
      setProgress(30);
    }
    if (navigation.state === "submitting") {
      setProgress(70);
    }
  }, [navigation.state]);

  useEffect(() => {
    if (!hasHydrated) return;
    const navElement = document.getElementById("navigation");
    const additionalOffset = isMobile ? 15 : 40;
    const handleScroll = () => {
      if (isTransparent) {
        return setShowFixedHeader(window.scrollY >= 100);
      }
      setShowFixedHeader(
        window.scrollY >= (navElement?.offsetTop || 0) + additionalOffset,
      );
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMobile, isTransparent, hasHydrated]);

  useEffect(() => {
    let lastScrollTop = 0;
    const handleScroll = () => {
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const availableHeight = document.documentElement.offsetHeight - window.innerHeight;

      if (availableHeight - currentScrollTop > 100) {
        setShowHeader(currentScrollTop < lastScrollTop);
      }
      lastScrollTop = currentScrollTop;
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <LayoutWrapper>
      <NewsLetterModal />
      <LoadingBar
        color="#27509B"
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />
      <Header
        basename={basename}
        data={data}
        language={language}
        showHeader={showHeader}
        showFixedHeader={showFixedHeader}
        styles={
          showHeader && showFixedHeader && headerType !== "logoOnly"
            ? {
                position: "sticky",
                zIndex: 2,
                backgroundColor: "white",
              }
            : {}
        }
        headerType={
          (showHeader && showFixedHeader) || (showMenu && isMobile) ? "white" : headerType
        }
        showMenu={showMenu}
        setShowMenu={setShowMenu}
        isSearchPage={isSearchPage}
      />

      {!isHomePage && !isLegalPage && !isSearchPage && !isContactPage && !isFaqPage && (
        <Breadcrumbs
          breadcrumb={localizeBreadcrumbMagazineName(breadcrumb, language)}
          isTransparent={isTransparent}
          isWhiteBg={isDestinationsPage}
        />
      )}

      {isArticlePage && (
        <>
          <StickyBar
            showFixedHeader={showFixedHeader}
            showHeader={showHeader}
            headerTitle={decodeHtmlEntities(data.sections?.post_title || "")}
          />
          <div id="navigation">
            <Navigation wordCount={wordCount} />
          </div>
        </>
      )}
      {isArticlePage && (
        <>
          <Ad
            type={"1000*90"}
            tag={BASE_AD_TAG["728*90"]}
            styles={{marginBlock: 40}}
            showAd={adsEnabled}
            bgColor={"gray"}
          />
          <B1Herobanner heroBannerData={heroBannerData} />
        </>
      )}
      {isDestinationPage && <HeroTransparent data={headerData} />}
      {isHomePage && <HeroTransparentText data={headerData} />}

      {children}

      <Footer
        language={language}
        secondLevelFooterLinks={secondLevelFooterLinks}
        footerLinks={footerLinks}
        footerData={footerData}
        headerType={headerType}
      />
    </LayoutWrapper>
  );
};

export default BasePage;
